import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, TOKEN_KEY, setUnauthorizedHandler } from "@/lib/api";

const AuthContext = createContext(undefined);
const IDLE_MINUTES = parseInt(process.env.REACT_APP_IDLE_LOGOUT_MIN || "30", 10);

export function AuthProvider({ children }) {
  // user: undefined = checking session, null = unauthenticated, object = authenticated
  const [user, setUser] = useState(undefined);
  const idleTimer = useRef(null);

  const clearSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const updateUser = useCallback((next) => setUser(next), []);

  const logout = useCallback(async () => {    try {
      await api.post("/auth/logout");
    } catch (_) {
      /* best-effort */
    }
    clearSession();
  }, [clearSession]);

  // On a 401 from any call, drop the session.
  useEffect(() => {
    setUnauthorizedHandler(clearSession);
  }, [clearSession]);

  // Restore session on mount.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => clearSession());
  }, [clearSession]);

  // Idle auto-logout.
  useEffect(() => {
    if (!user) return undefined;
    const reset = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => logout(), IDLE_MINUTES * 60 * 1000);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, idleMinutes: IDLE_MINUTES }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
